import React, { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FaTimes } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import API from 'core/Api';
import { createErrorFromResponse } from 'core/Error';

import { useModal } from 'store/modal';

import Form from 'components/form/Form';
import Button from 'components/Button';
import FormGroup from 'components/form/FormGroup';
import TooltipWrapper from 'components/TooltipWrapper';
import { archiveDoc, sendToEmployer } from 'routes/client/documents/helpers';
import handlePreviewDoc from 'utils/helpers/handlePreviewDoc';
import useInvalidateQueries from 'utils/helpers/useInvalidateQueries';

interface IProps {
  children?: any;
  id: string;
  docHolderId: string;
  buttonText: string;
  handlePreview: (docId: string) => void;
  invalidate: () => void;
  size?: 'sm' | 'lg';
  ariaLabel?: string;
  reason?: boolean | null;
}

interface IPropsReasonForm {
  handleReject?: any;
  docId: string;
  reason?: boolean | null;
}

interface IInputs {
  reason: string;
}

// Reject reason form
const ReasonForm: FC<IPropsReasonForm> = ({ handleReject, docId, reason = true }: IPropsReasonForm) => {
  const methods = useForm<IInputs>();
  const { hideModal } = useModal();
  const { t } = useTranslation();
  return (
    <>
      {reason !== false && <p className="text-center">{t('documents.table.confirmRejectReasonLabel')}</p>}
      <Form
        methods={methods}
        onSubmit={(data: IInputs) => handleReject(docId, data)}
        btnLabel={t('documents.table.confirmRejectDocLabel')}
        primaryActionBtnType="danger"
        onCancel={() => hideModal()}
      >
        {reason !== false && (
          <FormGroup
            controlId="formGroupReason"
            as="textarea"
            name="reason"
          />
        )}
      </Form>
    </>
  );
};

const RejectDoc: FC<IProps> = ({
  id,
  docHolderId,
  buttonText,
  handlePreview,
  invalidate,
  size = 'sm',
  ariaLabel,
  reason = true,
}: IProps) => {
  const { t } = useTranslation();
  const { showModal } = useModal();
  const [isLoading, setIsLoading] = useState(false);
  const invalidateQueries = useInvalidateQueries();

  // Reject document
  const handleReject = (docId: string, data: IInputs) => {
    const reasonValue = reason === false ? null : data.reason || null;
    API.post(`documents/${docId}/reject`, { reason: reasonValue })
      .then(res => {
        const { message } = res.data;
        showModal({
          type: 'success',
          content: t('documents.table.rejectedDocSuccessMsg'),
          onSuccess: () => {
            if (message?.type === 'archive') {
              archiveDoc(docHolderId, message.body, showModal, () => invalidateQueries([], invalidate), t);
            }
            if (message?.type === 'send_to_employer') {
              sendToEmployer(docHolderId, message.body, showModal, () => invalidateQueries([], invalidate), t);
            }
          },
        });
        invalidateQueries([], invalidate);
      })
      .catch(err =>
        showModal({
          type: 'error',
          content: createErrorFromResponse(err).message,
        })
      );
  };

  // Confirm rejection
  const confirmReject = (docId: string) => {
    showModal({
      type: 'form',
      heading: t('documents.table.confirmDocHeading'),
      content: (
        <>
          <p>{t('documents.table.confirmRejectDocMsg')}</p>
          <Button
            type="button"
            btnType="primary"
            outlined
            disabled={isLoading}
            onClick={() => handlePreviewDoc({ showModal, id: docId, setIsLoading })}
          >
            {t('documents.table.previewDoc')}
          </Button>
          <hr />
          <ReasonForm
            handleReject={handleReject}
            docId={docId}
            reason={reason}
          />
        </>
      ),
    });
  };

  return (
    <div className="d-inline-block ml-2 mb-2 mb-md-0">
      <TooltipWrapper
        id="tooltip-reject"
        overlay={buttonText}
      >
        <Button
          ariaLabel={ariaLabel}
          type="button"
          btnType="danger"
          size={size}
          onClick={() => confirmReject(id)}
        >
          <FaTimes size={14} />
        </Button>
      </TooltipWrapper>
    </div>
  );
};

export default RejectDoc;
